/**
 * countryProfiles
 * ---------------
 * Rend le moteur de conseils du « Mon Conseiller Patrimonial » MULTI-PAYS.
 *
 * Avant : tout le moteur déterministe (buildAdvisorFromReports.js) était codé
 * en dur pour la France et l'euro — formatage `… €`, barème fiscal FR, et
 * produits franco-français (Livret A, PER, PEA, assurance-vie). Pour un
 * utilisateur résidant hors de France / avec une autre devise, les conseils
 * étaient faux.
 *
 * Ce module centralise tout ce qui dépend du PAYS de résidence (`user.pays_id`)
 * et de la DEVISE (`user.currency_code`) :
 *   - le formatage monétaire (symbole + séparateurs via Intl) ;
 *   - une estimation de la tranche marginale d'imposition (TMI) par pays ;
 *   - le nom local des véhicules d'investissement par « pilier » patrimonial
 *     (épargne de précaution, retraite, enveloppe assurantielle, actions).
 *
 * Les produits portent des noms PROPRES (PER, 401(k), 3e pilier 3a…) qui sont
 * compréhensibles quelle que soit la langue d'affichage : on les passe donc
 * tels quels au moteur. Pour les pays non répertoriés, on retombe sur des noms
 * GÉNÉRIQUES traduits côté i18n (clé `generic`) pour rester lisibles.
 *
 * Important : ces profils restent des APPROXIMATIONS pédagogiques. Le détail
 * réglementaire fin (plafonds, fiscalité exacte) est volontairement délégué au
 * chat Life Agent (vrai LLM contextuel) qui connaît les spécificités par pays.
 */

// Piliers patrimoniaux universels. Le moteur raisonne par pilier ; chaque pays
// fournit le véhicule local correspondant.
//   precaution    → épargne de précaution / liquidités rémunérées
//   retirement    → épargne retraite (idéalement fiscalement avantagée)
//   lifeInsurance → enveloppe de capitalisation / assurantielle long terme
//   equity        → compte d'investissement actions / marchés

// Profils pays. `taxDeductible` indique si le véhicule retraite ouvre droit à
// une déduction du revenu imposable (déclenche le conseil d'optimisation
// fiscale). `name` = nom propre local ; à défaut `generic` = clé i18n.
//
// `realEstateIndex` : niveau RELATIF des prix de l'immobilier résidentiel,
// base France = 1.0. Sert à dimensionner le « prix cible » d'une résidence
// principale de façon réaliste selon le pays (un T3 à Lausanne ≠ un T3 à
// Limoges). Valeurs volontairement approximatives (ordres de grandeur).
const COUNTRY_PROFILES = {
  FR: {
    locale: 'fr-FR',
    currency: 'EUR',
    realEstateIndex: 1.0,
    products: {
      precaution: { name: 'Livret A' },
      retirement: { name: 'PER', taxDeductible: true },
      lifeInsurance: { name: 'assurance-vie' },
      equity: { name: 'PEA' },
    },
  },
  BE: {
    locale: 'fr-BE',
    currency: 'EUR',
    realEstateIndex: 0.95,
    products: {
      precaution: { generic: 'precaution' },
      retirement: { name: 'épargne-pension (3e pilier)', taxDeductible: true },
      lifeInsurance: { name: 'assurance-vie (branche 21/23)' },
      equity: { name: 'compte-titres' },
    },
  },
  CH: {
    locale: 'fr-CH',
    currency: 'CHF',
    realEstateIndex: 2.3,
    products: {
      precaution: { generic: 'precaution' },
      retirement: { name: '3e pilier (3a)', taxDeductible: true },
      lifeInsurance: { name: 'assurance-vie' },
      equity: { name: 'compte-titres' },
    },
  },
  LU: {
    locale: 'fr-LU',
    currency: 'EUR',
    realEstateIndex: 1.9,
    products: {
      precaution: { generic: 'precaution' },
      retirement: { name: 'prévoyance-vieillesse', taxDeductible: true },
      lifeInsurance: { name: 'assurance-vie' },
      equity: { name: 'compte-titres' },
    },
  },
  DE: {
    locale: 'de-DE',
    currency: 'EUR',
    realEstateIndex: 1.1,
    products: {
      precaution: { name: 'Tagesgeldkonto' },
      retirement: { name: 'Riester-/Rürup-Rente', taxDeductible: true },
      lifeInsurance: { name: 'Lebensversicherung' },
      equity: { name: 'ETF-Depot' },
    },
  },
  ES: {
    locale: 'es-ES',
    currency: 'EUR',
    realEstateIndex: 0.8,
    products: {
      precaution: { generic: 'precaution' },
      retirement: { name: 'plan de pensiones', taxDeductible: true },
      lifeInsurance: { name: 'seguro de vida-ahorro' },
      equity: { name: 'cuenta de valores' },
    },
  },
  IT: {
    locale: 'it-IT',
    currency: 'EUR',
    realEstateIndex: 0.85,
    products: {
      precaution: { name: 'conto deposito' },
      retirement: { name: 'fondo pensione', taxDeductible: true },
      lifeInsurance: { name: 'polizza vita' },
      equity: { name: 'conto titoli' },
    },
  },
  PT: {
    locale: 'pt-PT',
    currency: 'EUR',
    realEstateIndex: 0.75,
    products: {
      precaution: { generic: 'precaution' },
      retirement: { name: 'PPR', taxDeductible: true },
      lifeInsurance: { name: 'seguro de vida' },
      equity: { name: 'conta de títulos' },
    },
  },
  GB: {
    locale: 'en-GB',
    currency: 'GBP',
    realEstateIndex: 1.4,
    products: {
      precaution: { name: 'Cash ISA' },
      retirement: { name: 'SIPP / workplace pension', taxDeductible: true },
      lifeInsurance: { name: 'investment bond' },
      equity: { name: 'Stocks & Shares ISA' },
    },
  },
  US: {
    locale: 'en-US',
    currency: 'USD',
    realEstateIndex: 1.3,
    products: {
      precaution: { name: 'high-yield savings account' },
      retirement: { name: '401(k) / IRA', taxDeductible: true },
      lifeInsurance: { name: 'cash-value life insurance' },
      equity: { name: 'brokerage account' },
    },
  },
  CA: {
    locale: 'en-CA',
    currency: 'CAD',
    realEstateIndex: 1.2,
    products: {
      precaution: { name: 'TFSA savings' },
      retirement: { name: 'RRSP', taxDeductible: true },
      lifeInsurance: { name: 'permanent life insurance' },
      equity: { name: 'non-registered brokerage' },
    },
  },
}

// Profil par défaut : pays inconnu → noms de produits GÉNÉRIQUES (résolus via
// i18n, clé `generic`) et devise = celle de l'utilisateur (currency_code).
const DEFAULT_PROFILE = {
  locale: undefined, // résolu depuis la devise/navigateur au moment du format
  currency: 'EUR',
  realEstateIndex: 1.0,
  products: {
    precaution: { generic: 'precaution' },
    retirement: { generic: 'retirement', taxDeductible: false },
    lifeInsurance: { generic: 'lifeInsurance' },
    equity: { generic: 'equity' },
  },
}

// Locale d'affichage « raisonnable » selon la devise, pour les groupes de
// milliers et la position du symbole quand le pays n'est pas répertorié.
const CURRENCY_LOCALE = {
  EUR: 'fr-FR',
  USD: 'en-US',
  GBP: 'en-GB',
  CHF: 'de-CH',
  CAD: 'en-CA',
  JPY: 'ja-JP',
  CNY: 'zh-CN',
  AUD: 'en-AU',
  AED: 'ar-AE',
}

function normalizeCountry(paysId) {
  if (!paysId) return null
  return String(paysId).trim().toUpperCase().slice(0, 2)
}

function normalizeCurrency(code) {
  if (!code) return null
  const c = String(code).trim().toUpperCase()
  return /^[A-Z]{3}$/.test(c) ? c : null
}

/**
 * Construit une fonction de formatage monétaire pour une devise donnée.
 * Conserve le style « compact » (1,2 k€ / $1.2K) de l'ancien `formatEuro`,
 * mais avec le bon symbole et les bons séparateurs via Intl. Tombe en repli
 * sur un format simple si la plateforme ne supporte pas `notation: compact`.
 */
export function makeMoneyFormatter(currency, locale) {
  const cur = normalizeCurrency(currency) || 'EUR'
  const loc = locale || CURRENCY_LOCALE[cur] || 'en-US'
  let compact = null
  let plain = null
  try {
    compact = new Intl.NumberFormat(loc, {
      style: 'currency',
      currency: cur,
      notation: 'compact',
      maximumFractionDigits: 1,
    })
  } catch (e) {
    compact = null
  }
  try {
    plain = new Intl.NumberFormat(loc, {
      style: 'currency',
      currency: cur,
      maximumFractionDigits: 0,
    })
  } catch (e) {
    plain = null
  }
  return function money(n) {
    if (n == null || !Number.isFinite(Number(n))) return '—'
    const value = Number(n)
    const abs = Math.abs(value)
    try {
      // Au-delà de 10 000, la notation compacte reste lisible ; en deçà on
      // préfère le montant exact (plus parlant pour des versements mensuels).
      if (abs >= 10000 && compact) return compact.format(value)
      if (plain) return plain.format(Math.round(value))
    } catch (e) {
      /* repli ci-dessous */
    }
    return `${Math.round(value)} ${cur}`
  }
}

/**
 * Résout le profil pays/devise effectif d'un utilisateur.
 * - `paysId`    : code pays de résidence (`user.pays_id`).
 * - `currencyCode` : devise de l'utilisateur (`user.currency_code`), qui prime
 *   TOUJOURS pour l'affichage (un résident FR peut compter en USD).
 */
export function getCountryProfile({ paysId, currencyCode } = {}) {
  const country = normalizeCountry(paysId)
  const userCurrency = normalizeCurrency(currencyCode)
  const base = (country && COUNTRY_PROFILES[country]) || DEFAULT_PROFILE
  const currency = userCurrency || base.currency || 'EUR'
  const locale = base.locale || CURRENCY_LOCALE[currency] || undefined

  return {
    code: country || 'INT',
    isKnownCountry: !!(country && COUNTRY_PROFILES[country]),
    currency,
    locale,
    products: base.products,
    // Niveau relatif des prix immobiliers (base FR = 1.0). Sert à dimensionner
    // un prix cible de résidence principale cohérent avec le pays.
    realEstateIndex: base.realEstateIndex || 1.0,
    formatMoney: makeMoneyFormatter(currency, locale),
    estimateTmi(annualIncome) {
      return estimateTmiForCountry(country, annualIncome)
    },
    // Nom local d'un véhicule pour un pilier donné. `t` = fonction i18n ;
    // pour les pays connus on renvoie le nom propre, sinon le terme générique
    // traduit dans la langue d'affichage.
    productName(pillar, t) {
      const p = (base.products && base.products[pillar]) || {}
      if (p.name) return p.name
      const key = p.generic || pillar
      if (typeof t === 'function') {
        return t(`conseillerPatrimonial.productGeneric.${key}`, {
          defaultValue: GENERIC_FALLBACK[key] || key,
        })
      }
      return GENERIC_FALLBACK[key] || key
    },
    isTaxDeductibleRetirement() {
      const r = (base.products && base.products.retirement) || {}
      return !!r.taxDeductible
    },
  }
}

// Repli ultime (si t indisponible) pour les noms génériques.
const GENERIC_FALLBACK = {
  precaution: 'a regulated savings account',
  retirement: 'a tax-advantaged retirement account',
  lifeInsurance: 'a life-insurance investment wrapper',
  equity: 'a stock investment account',
}

/**
 * Estimation grossière de la TMI (0..1) selon le pays. Valeurs volontairement
 * approximatives et conservatrices — l'objectif est de NE PLUS appliquer le
 * barème français à tout le monde, pas de remplacer un conseiller fiscal.
 * Les revenus sont annuels, exprimés dans la devise de l'utilisateur (on
 * raisonne sur des ordres de grandeur, sans conversion de change).
 */
function estimateTmiForCountry(country, annualIncome) {
  const income = Number(annualIncome) || 0
  if (income <= 0) return 0

  // Barèmes simplifiés (seuils approximatifs, célibataire).
  const SCALES = {
    FR: [
      [11500, 0],
      [29000, 0.11],
      [83000, 0.3],
      [180000, 0.41],
      [Infinity, 0.45],
    ],
    BE: [
      [15000, 0.25],
      [27000, 0.4],
      [47000, 0.45],
      [Infinity, 0.5],
    ],
    DE: [
      [11600, 0],
      [66800, 0.24],
      [277000, 0.42],
      [Infinity, 0.45],
    ],
    ES: [
      [12450, 0.19],
      [35200, 0.3],
      [60000, 0.37],
      [300000, 0.45],
      [Infinity, 0.47],
    ],
    IT: [
      [28000, 0.23],
      [50000, 0.35],
      [Infinity, 0.43],
    ],
    PT: [
      [21300, 0.23],
      [39800, 0.35],
      [80000, 0.45],
      [Infinity, 0.48],
    ],
    CH: [
      [30000, 0.05],
      [80000, 0.15],
      [150000, 0.25],
      [Infinity, 0.35],
    ],
    GB: [
      [12570, 0],
      [50270, 0.2],
      [125140, 0.4],
      [Infinity, 0.45],
    ],
    US: [
      [11600, 0.1],
      [47150, 0.12],
      [100525, 0.22],
      [191950, 0.24],
      [243725, 0.32],
      [609350, 0.35],
      [Infinity, 0.37],
    ],
    CA: [
      [55900, 0.15],
      [111700, 0.205],
      [173200, 0.26],
      [246800, 0.29],
      [Infinity, 0.33],
    ],
  }
  const scale = SCALES[country] || SCALES.FR
  for (const [ceiling, rate] of scale) {
    if (income < ceiling) return rate
  }
  return scale[scale.length - 1][1]
}
